import React, { useEffect, useState, useMemo } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
    card: "#FFFFFF",
    border: "#E5E7EB",
    muted: "#9CA3AF",
    text: "#111827",
    textSoft: "#6B7280",
    dark: "#F9FAFB",
    amber: "#D97706",
    teal: "#0D9488",
    coral: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    purple: "#7C3AED",
}

// ── Shared UI ──────────────────────────────────────────────────────────────
const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-5">
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="mt-1 mb-0.5 text-[22px] font-bold leading-none truncate" style={{ color }}>{value}</p>
        {sub && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const StatusChip = ({ status }) => {
    const cfg = {
        present:  { label: 'Present',  color: C.green  },
        late:     { label: 'Late',     color: C.amber  },
        absent:   { label: 'Absent',   color: C.coral  },
        on_leave: { label: 'On Leave', color: C.purple },
        waiting:  { label: 'Waiting',  color: C.muted  },
        not_yet:  { label: 'Not Yet',  color: C.muted  },
        day_off:  { label: 'Day Off',  color: C.blue   },
    }[status] ?? { label: status, color: C.muted }
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: cfg.color + '22', color: cfg.color }}>
            {cfg.label}
        </span>
    )
}

const OT_THRESHOLD_MS = 15 * 60 * 1000 // 15 min grace before flagging OT/UT

const getOvertimeStatus = (record) => {
    if (!record?.clock_in || !record?.clock_out || !record?.scheduled_in || !record?.scheduled_out) return null
    const actualMs    = new Date(record.clock_out) - new Date(record.clock_in)
    const scheduledMs = new Date(record.scheduled_out) - new Date(record.scheduled_in)
    const diff = actualMs - scheduledMs
    if (diff > OT_THRESHOLD_MS)  return { type: 'overtime',  diffMs: diff }
    if (diff < -OT_THRESHOLD_MS) return { type: 'undertime', diffMs: Math.abs(diff) }
    return null
}

const OvertimeChip = ({ record }) => {
    const ot = getOvertimeStatus(record)
    if (!ot) return <span className="text-[10px]" style={{ color: C.muted }}>—</span>
    const mins = Math.round(ot.diffMs / 60000)
    const h = Math.floor(mins / 60), m = mins % 60
    const label = h > 0 ? `${h}h ${m}m` : `${m}m`
    const isOver = ot.type === 'overtime'
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: (isOver ? C.blue : C.coral) + '22', color: isOver ? C.blue : C.coral }}>
            {isOver ? `+${label} OT` : `-${label} UT`}
        </span>
    )
}

// ── Photo Modal ────────────────────────────────────────────────────────────
function PhotoModal({ url, label, onClose }) {
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)' }}
            onClick={onClose}>
            <div className="relative flex flex-col items-center gap-3"
                onClick={e => e.stopPropagation()}>
                <button onClick={onClose}
                    className="absolute -top-10 right-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
                <img src={url} alt={label}
                    className="rounded-2xl shadow-2xl object-contain"
                    style={{ maxWidth: '90vw', maxHeight: '80vh' }} />
                <span className="text-sm font-semibold text-white/70">{label}</span>
            </div>
        </div>
    )
}

// ── Helpers ────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0')

// Local (browser/PH) calendar date — avoids the UTC-offset bug where
// toISOString() rolls back to "yesterday" during PH early-morning hours
const getLocalDateStr = (d = new Date()) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const fmtDuration = (ms) => {
    if (!ms || ms <= 0) return '—'
    const totalMins = Math.floor(ms / 60000)
    const h = Math.floor(totalMins / 60)
    const m = totalMins % 60
    return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`
}

const fmtCurrency = (n) =>
    n == null ? '—' : `₱${Number(n).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`

const presetStart = (range) => {
    if (range === 'all') return null
    const days = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 }[range] ?? 30
    return new Date(Date.now() - days * 86400000)
}

// ── Today-status derivation (mirrors ShiftAttendance) ─────────────────────
const ENABLE_BEFORE_MS = 60 * 60 * 1000

const deriveTodayStatus = (record, now, schedule) => {
    if (record?.clock_in) return null

    // Check day off first
    const dateStr = record?.date ?? new Date().toISOString().slice(0, 10)
    if (isDayOff(dateStr, schedule)) return 'day_off'

    const scheduled_in  = record?.scheduled_in  ? new Date(record.scheduled_in)  : null
    const scheduled_out = record?.scheduled_out ? new Date(record.scheduled_out) : null

    if (!scheduled_in) return 'waiting'
    if (now < new Date(scheduled_in.getTime() - ENABLE_BEFORE_MS)) return 'not_yet'
    if (now < scheduled_in) return 'waiting'
    if (now >= scheduled_in && (!scheduled_out || now < scheduled_out)) return 'late'
    if (scheduled_out && now >= scheduled_out) return 'absent'
    return 'waiting'
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const isDayOff = (dateStr, schedule) => {
    if (!schedule?.work_days?.length) return false
    const dayKey = DAY_KEYS[new Date(dateStr + 'T00:00:00').getDay()]
    return !schedule.work_days.includes(dayKey)
}

// ── Range Selector  ────────────────────────────────────────────────────────────────
const RangeSelector = ({ range, setRange, customStart, setCustomStart, customEnd, setCustomEnd }) => {
    const options = [
        { label: 'All Time', value: 'all'},
        { label: '7 days', value: '7d' },
        { label: '30 days', value: '30d' },
        { label: '90 days', value: '90d' },
        { label: '1 year', value: '1y' },
        { label: 'Custom', value: 'custom' },
    ]

    const handlePreset = (value) => {
        setRange(value)
        if (value !== 'custom') {
            setCustomStart('')
            setCustomEnd('')
        }
    }

    const hasCustom = customStart && customEnd

    return (
        <div className="flex flex-col gap-2 items-end">
            <div className="flex flex-wrap gap-1.5 justify-end">
                {options.map(o => (
                    <button key={o.value} onClick={() => handlePreset(o.value)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                        style={{
                            background:  range === o.value && !hasCustom ? C.amber : C.card,
                            borderColor: range === o.value && !hasCustom ? C.amber : C.border,
                            color:       range === o.value && !hasCustom ? '#fff'  : C.textSoft,
                            cursor: 'pointer',
                        }}>
                        {o.label}
                    </button>
                ))}
            </div>

            {/* Custom date pickers */}
            {(range === 'custom' || hasCustom) && (
                <div className="flex items-center gap-2 flex-wrap justify-end pt-1.5 border-t border-dashed"
                    style={{ borderColor: C.border }}>
                    <span className="text-[11px]" style={{ color: C.muted }}>From:</span>
                    <input
                        type="date"
                        value={customStart}
                        onChange={e => setCustomStart(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border, color: C.text, background: C.card }}
                    />
                    <span className="text-[11px]" style={{ color: C.muted }}>To:</span>
                    <input
                        type="date"
                        value={customEnd}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={e => setCustomEnd(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border, color: C.text, background: C.card }}
                    />
                    {hasCustom && (
                        <button
                            onClick={() => { setCustomStart(''); setCustomEnd(''); setRange('all') }}
                            className="text-[11px] px-2 py-1 rounded-full border"
                            style={{ borderColor: C.border, color: C.muted, cursor: 'pointer', background: C.card }}>
                            ✕ Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

// ── Date-range boundary ────────────────────────────────────────────────────────────────
const useDateBounds = (range, customStart, customEnd) => {
    return useMemo(() => {
        const hasCustom = customStart && customEnd
        if (hasCustom) {
            return {
                startBound: new Date(customStart),
                endBound:   new Date(customEnd + 'T23:59:59'),
            }
        }
        return {
            startBound: presetStart(range),
            endBound:   null,
        }
    }, [range, customStart, customEnd])
}

const filterByDate = (records, dateField, startBound, endBound) => {
    if (!startBound && !endBound) return records
    return records.filter(r => {
        const d = new Date(r[dateField])
        if (startBound && d < startBound) return false
        if (endBound   && d > endBound) return false
        return true
    })
}

// Period label
const periodLabel = (startBound, endBound) => {
    if (!startBound) return 'All Time'
    const fmt = (d) => d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
    return `${fmt(startBound)} — ${endBound ? fmt(endBound) : 'Today'}`
}

// Build per-staff order / POS 
const buildOrderMaps = (staffIds, filteredOrders) => {
    const oMap = {}, pMap = {}
    staffIds.forEach(id => {
        oMap[id] = { count: 0, revenue: 0 }
        pMap[id] = { count: 0, revenue: 0 }
    })
    filteredOrders.forEach(o => {
        if (!o.staff_id) return
        if (o.order_source === 'pos') {
            pMap[o.staff_id].count   += 1
            pMap[o.staff_id].revenue += Number(o.final_amount ?? 0)
        } else {
            oMap[o.staff_id].count   += 1
            oMap[o.staff_id].revenue += Number(o.final_amount ?? 0)
        }
    })
    return { oMap, pMap }
}

const buildBaristaOrderMaps = (baristaIds, filteredOrders) => {
    const ordersMap = {}, posMap = {}
    baristaIds.forEach(id => {
        ordersMap[id] = { count: 0, revenue: 0 }
        posMap[id]    = { count: 0, revenue: 0 }
    })
    filteredOrders.forEach(o => {
        // order_completed_by = who completed/managed the order
        if (o.order_completed_by && ordersMap[o.order_completed_by]) {
            ordersMap[o.order_completed_by].count   += 1
            ordersMap[o.order_completed_by].revenue += Number(o.final_amount ?? 0)
        }
        // pos_processed_by = who handled it on POS
        if (o.pos_processed_by && posMap[o.pos_processed_by]) {
            posMap[o.pos_processed_by].count   += 1
            posMap[o.pos_processed_by].revenue += Number(o.final_amount ?? 0)
        }
    })
    return { prepMap: ordersMap, posMap }
}

// ── Avatar ─────────────────────────────────────────────────────────────────
const Avatar = ({ firstName, lastName, profilePic, size = 36 }) => {
    const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()
    if (profilePic) {
        return (
            <img src={profilePic} alt={initials}
                className="rounded-full object-cover flex-shrink-0"
                style={{ width: size, height: size }} />
        )
    }
    const colors = [C.amber, C.teal, C.blue, C.purple, C.green, C.coral]
    const idx = (firstName?.charCodeAt(0) ?? 0) % colors.length
    return (
        <div className="rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white"
            style={{ width: size, height: size, background: colors[idx], fontSize: size * 0.35 }}>
            {initials}
        </div>
    )
}

// ── Branch-level KPI summary ───────────────────────────────────────────────
function BranchSummaryBar({ staffList, baristaList, rawAttendanceMap, rawBaristaAttendanceMap, ordersMap, baristaOrdersMap, posMap, baristaPosMap, startBound, endBound }) {
    const today = getLocalDateStr()

    const totalStaff = staffList.length + baristaList.length

    const presentToday =
        staffList.filter(s =>
            rawAttendanceMap[s.id]?.some(r => r.date === today && (r.status === 'present' || r.status === 'late'))
        ).length +
        baristaList.filter(b =>
            rawBaristaAttendanceMap[b.barista_id]?.some(r => r.date === today && (r.status === 'present' || r.status === 'late'))
        ).length

    const totalOrders =
        staffList.reduce((sum, s) => sum + (ordersMap[s.id]?.count ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaOrdersMap[b.barista_id]?.count ?? 0), 0)

    const totalRevenue =
        staffList.reduce((sum, s) => sum + (ordersMap[s.id]?.revenue ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaOrdersMap[b.barista_id]?.revenue ?? 0), 0)

    const totalPOS =
        staffList.reduce((sum, s) => sum + (posMap[s.id]?.count ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaPosMap[b.barista_id]?.count ?? 0), 0)

    const totalPOSRev =
        staffList.reduce((sum, s) => sum + (posMap[s.id]?.revenue ?? 0), 0) +
        baristaList.reduce((sum, b) => sum + (baristaPosMap[b.barista_id]?.revenue ?? 0), 0)

    return (
        <Card>
            <SectionTitle
                title="Branch Overview"
                subtitle={periodLabel(startBound, endBound)}
            />
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                <KPI label="Total Staff" value={totalStaff} color={C.blue}   />
                <KPI label="Present Today" value={presentToday} color={C.green}  sub={`of ${totalStaff}`} />
                <KPI label="Orders Managed" value={totalOrders} color={C.amber}  sub="all staff" />
                <KPI label="Order Revenue" value={fmtCurrency(totalRevenue)} color={C.teal}   sub="all staff" />
                <KPI label="POS Transactions" value={totalPOS} color={C.purple} sub={fmtCurrency(totalPOSRev)} />
            </div>
        </Card>
    )
}

function ActivePOSBadge({ branchId }) {
    const [active, setActive] = useState(null) // { barista, heartbeatAt } | null

    const fetchActive = async () => {
        if (!branchId) return
        const { data } = await supabase
            .from('branches')
            .select('pos_last_heartbeat, active_pos_barista_id, barista_info:active_pos_barista_id ( first_name, last_name )')
            .eq('branch_id', branchId)
            .maybeSingle()
        if (!data?.active_pos_barista_id) { setActive(null); return }
        setActive({ barista: data.barista_info, heartbeatAt: data.pos_last_heartbeat })
    }

    useEffect(() => {
        fetchActive()
        const interval = setInterval(fetchActive, 15000) // refresh faster than the 20s heartbeat
        return () => clearInterval(interval)
    }, [branchId])

    if (!active?.barista) {
        return (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-medium" style={{ borderColor: C.border, color: C.muted, background: C.dark }}>
                <span className="w-2 h-2 rounded-full" style={{ background: C.muted }} />
                No one currently on POS
            </div>
        )
    }

    const STALE_MS = 40000 // heartbeat pings every 20s — treat >40s old as stale/gone
    const isStale = !active.heartbeatAt || (Date.now() - new Date(active.heartbeatAt).getTime() > STALE_MS)

    return (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-medium" style={{ borderColor: C.border, background: C.card }}>
            <span className={`w-2 h-2 rounded-full ${isStale ? '' : 'animate-pulse'}`} style={{ background: isStale ? C.muted : C.green }} />
            <span style={{ color: C.text }}>
                {isStale ? 'Last on POS' : 'Currently on POS'}: <strong>{active.barista.first_name} {active.barista.last_name}</strong>
            </span>
        </div>
    )
}

// ── Remake Offender Detail Modal ────────────────────────────────────────
function RemakeOffenderDetailModal({ offender, requests, onClose }) {
    const relatedRequests = useMemo(() =>
        requests
            .filter(r => (r.responsible_staff || []).some(s => s.staff_id === offender.staff_id))
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [requests, offender.staff_id])

    const [page, setPage] = useState(1)
    const PAGE_SIZE = 10

    const pagedRequests = useMemo(() =>
        relatedRequests.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [relatedRequests, page])

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: C.border }}>
                    <div>
                        <p className="m-0 text-base font-bold" style={{ color: C.text }}>{offender.name}</p>
                        <p className="m-0 text-xs mt-0.5" style={{ color: C.muted }}>
                            {offender.role} · {relatedRequests.length} remake{relatedRequests.length !== 1 ? 's' : ''} flagged
                        </p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm">✕</button>
                </div>

                <div className="p-6 space-y-3">
                    {relatedRequests.length === 0 ? (
                        <p className="text-xs text-center py-6" style={{ color: C.muted }}>No remake records found for this period.</p>
                    ) : (
                        pagedRequests.map(r => (
                            <div key={r.remake_request_id} className="rounded-xl border p-3" style={{ borderColor: C.border }}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-semibold" style={{ color: C.text }}>
                                        Order #{r.reference_number || '—'}
                                    </span>
                                    <span className="text-[11px]" style={{ color: C.muted }}>
                                        {new Date(r.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        {' · '}
                                        {new Date(r.created_at).toLocaleTimeString('en-PH', { hour: 'numeric', minute: '2-digit' })}
                                    </span>
                                </div>
                                <p className="text-xs" style={{ color: C.textSoft }}>Reason: {r.reason}</p>
                                {r.responsible_staff?.length > 1 && (
                                    <p className="text-[11px] mt-1" style={{ color: C.muted }}>
                                        Also flagged: {r.responsible_staff.filter(s => s.staff_id !== offender.staff_id).map(s => s.name).join(', ') || '—'}
                                    </p>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <Pagination
                    page={page}
                    totalItems={relatedRequests.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                />

                <div className="px-6 py-4 border-t flex justify-end" style={{ borderColor: C.border }}>
                    <button onClick={onClose} className="px-5 py-2 rounded-lg font-medium text-sm text-white" style={{ background: C.text }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

function Pagination({ page, totalItems, pageSize, onPageChange }) {
    const totalPages = Math.ceil(totalItems / pageSize)
    if (totalPages <= 1) return null

    return (
        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center justify-between">
            <p className="text-xs text-gray-600">
                Showing {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems}
            </p>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className="px-2.5 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                   ← Prev
                </button>
                {(() => {
                    const pages = []
                    const delta = 1

                    let start = Math.max(2, page - delta)
                    let end = Math.min(totalPages - 1, page + delta)

                    pages.push(1)

                    if (start > 2) pages.push('...')

                    for (let n = start; n <= end; n++) pages.push(n)

                    if (end < totalPages - 1) pages.push('...')

                    if (totalPages > 1) pages.push(totalPages)

                    return pages.map((n, i) =>
                        n === '...' ? (
                            <span key={`ellipsis-${i}`} className="px-1.5 py-1 text-xs text-gray-400">…</span>
                        ) : n === page ? (
                            <button key={n} onClick={() => onPageChange(n)}
                                className="w-6 h-6 flex items-center justify-center bg-[#A0826D] text-white rounded-md text-xs font-medium">
                                {n}
                            </button>
                        ) : (
                            <button key={n} onClick={() => onPageChange(n)}
                                className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50">
                                {n}
                            </button>
                        )
                    )
                })()}
                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className="px-2.5 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Next →
                </button>
            </div>
        </div>
    )
}

// ── Top Remake Offenders ────────────────────────────────────────────────
function TopRemakeOffendersCard({ branchId, startBound, endBound }) {
    const [requests, setRequests] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedOffender, setSelectedOffender] = useState(null)

    useEffect(() => {
        if (!branchId) return
        const fetchRequests = async () => {
            setLoading(true)
            const { data, error } = await supabase
                .from('remake_requests')
                .select('remake_request_id, reference_number, reason, responsible_staff, created_at')
                .eq('branch_id', branchId)
                .order('created_at', { ascending: false })
            if (error) console.error('Error fetching remake requests for leaderboard:', error)
            setRequests(data ?? [])
            setLoading(false)
        }
        fetchRequests()
    }, [branchId])

    const tally = useMemo(() => {
        const filtered = filterByDate(requests, 'created_at', startBound, endBound)
        const counts = {}
        filtered.forEach(r => {
            ;(r.responsible_staff || []).forEach(s => {
                if (!s?.staff_id) return
                if (!counts[s.staff_id]) counts[s.staff_id] = { staff_id: s.staff_id, name: s.name, role: s.role, count: 0 }
                counts[s.staff_id].count += 1
            })
        })
        return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 5)
        }, [requests, startBound, endBound])

        if (loading) return <Card><SectionTitle title="Top Remake Offenders" /><Spinner height={120} /></Card>

        return (
            <Card>
                <SectionTitle title="Top Remake Offenders" subtitle="Baristas/staff most often flagged as responsible for a remake — click a row for details" />
                {selectedOffender && (
                    <RemakeOffenderDetailModal
                        offender={selectedOffender}
                        requests={filterByDate(requests, 'created_at', startBound, endBound)}
                        onClose={() => setSelectedOffender(null)}
                    />
                )}
                {tally.length === 0 ? (
                    <p className="text-xs py-4 text-center" style={{ color: C.muted }}>No remake requests recorded for this period.</p>
                ) : (
                    <div className="space-y-1.5">
                        {tally.map((entry, idx) => (
                            <div key={entry.staff_id}
                                onClick={() => setSelectedOffender(entry)}
                                className="flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
                                style={{ borderColor: C.border }}>
                                <div className="flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                                        style={{ background: idx === 0 ? C.coral : C.muted }}>
                                        {idx + 1}
                                    </span>
                                    <div>
                                        <p className="m-0 text-xs font-semibold" style={{ color: C.text }}>{entry.name}</p>
                                        <p className="m-0 text-[10px]" style={{ color: C.muted }}>{entry.role}</p>
                                    </div>
                                </div>
                                <span className="text-xs font-bold" style={{ color: C.coral }}>
                                    {entry.count} remake{entry.count !== 1 ? 's' : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        )
}

// ── Staff detail drawer ────────────────────────────────────────────────────
function StaffDrawer({ staff, attendance, orders, pos, processed, schedule, onClose, onRefetch, startBound, endBound, isBarista, onViewPhoto }) {    
    const [showLeaveForm, setShowLeaveForm] = useState(false)
    const [leaveForm, setLeaveForm] = useState({ start_date: '', end_date: '', reason: '' })
    const [savingLeave, setSavingLeave] = useState(false)
    const [leaveError, setLeaveError] = useState(null)
    const [deletingLeaveId, setDeletingLeaveId] = useState(null)

    const windowAtt = useMemo(() =>
        filterByDate(attendance ?? [], 'date', startBound, endBound),
    [attendance, startBound, endBound])

    const present = windowAtt.filter(r => r.status === 'present').length
    const late = windowAtt.filter(r => r.status === 'late').length
    const absent = windowAtt.filter(r => r.status === 'absent').length
    const onLeave = windowAtt.filter(r => r.status === 'on_leave').length
    const totalMs = windowAtt
        .filter(r => r.clock_in && r.clock_out)
        .reduce((s, r) => s + (new Date(r.clock_out) - new Date(r.clock_in)), 0)
    const worked = present + late
    const rate = windowAtt.length ? Math.round((worked / windowAtt.length) * 100) : 0

    const todayStr = getLocalDateStr()
    const sortedHistory = [...(attendance ?? [])]
        .filter(r => r.date <= todayStr)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 10)

    const fmtTime = (iso) => {
        if (!iso) return '—'
        const d = new Date(iso)
        const h = d.getHours(), m = d.getMinutes()
        return `${pad(h % 12 || 12)}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`
    }
    const fmtDate = (iso) => {
        if (!iso) return '—'
        return new Date(iso).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    const period = periodLabel(startBound, endBound)

    const handleSaveLeave = async () => {
        if (!leaveForm.start_date || !leaveForm.end_date) return
        setSavingLeave(true)
        try {
            // Build the list of dates in range
            const start = new Date(leaveForm.start_date)
            const end   = new Date(leaveForm.end_date)
            const dates = []
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                dates.push(d.toISOString().slice(0, 10))
            }

            // Check for existing on_leave records on any of those dates
            const idCol = isBarista ? 'barista_id' : 'staff_id'
            const { data: existing, error: checkError } = await supabase
                .from('staff_attendance')
                .select('date, status, clock_in')
                .eq(idCol, staff.id)
                .in('date', dates)

            if (checkError) throw checkError

            const leaveConflicts = (existing ?? []).filter(r => r.status === 'on_leave')
            const attendedConflicts = (existing ?? []).filter(r => r.status !== 'on_leave' && r.clock_in)

            if (leaveConflicts.length > 0) {
                setLeaveError(`Leave already scheduled on: ${leaveConflicts.map(r =>
                    new Date(r.date).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
                ).join(', ')}. Please choose different dates.`)
                setSavingLeave(false)
                return
            }

            if (attendedConflicts.length > 0) {
                setLeaveError(`Can't mark leave — already clocked in on: ${attendedConflicts.map(r =>
                    new Date(r.date).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
                ).join(', ')}. Adjust the range or fix those records first.`)
                setSavingLeave(false)
                return
            }

            setLeaveError(null)
            const rows = dates.map(dateStr => ({
                [idCol]: staff.id,
                branch_id: staff.branch_id,
                date: dateStr,
                status: 'on_leave',
                notes: leaveForm.reason || null,
            }))
            const { error } = await supabase
                .from('staff_attendance')
                .upsert(rows, { onConflict: `${idCol},date` })
            if (error) throw error

            setLeaveForm({ start_date: '', end_date: '', reason: '' })
            setLeaveError(null)
            setShowLeaveForm(false)
            // Pull the freshly-saved rows back in immediately — without this,
            // the new leave only shows up after closing and reopening the drawer.
            await onRefetch?.()
        } catch (err) {
            setLeaveError(err.message)
        } finally {
            setSavingLeave(false)
        }
    }

    const handleDeleteLeave = async (record) => {
        setDeletingLeaveId(record.id)
        try {
            // NOTE: no .select() here to verify the delete. Under RLS,
            // DELETE...RETURNING requires a SELECT policy to match the
            // (now-deleted) row to return it — so even when the delete
            // succeeds, the returned rows can come back empty and make a
            // successful delete look like a permissions failure. The only
            // reliable signal here is `error`; we confirm the real outcome
            // by re-fetching from the DB below.
            const { error } = await supabase
                .from('staff_attendance')
                .delete()
                .eq('id', record.id)
            if (error) throw error

            setLeaveError(null)
            // Re-fetch instead of just filtering local state, so what's on
            // screen always matches what's actually in the database. If the
            // delete was silently blocked (e.g. by a DELETE RLS policy),
            // the record will simply still be present after this refetch.
            await onRefetch?.()
        } catch (err) {
            console.error('Delete leave error:', err)
            setLeaveError(err.message)
        } finally {
            setDeletingLeaveId(null)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={onClose}>
            <div className="ml-auto w-full max-w-2xl h-full overflow-y-auto shadow-2xl bg-white flex flex-col"
                style={{ borderLeft: `1px solid ${C.border}` }}
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between mb-4 gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                        <Avatar firstName={staff.first_name} lastName={staff.last_name}
                            profilePic={staff.profile_picture_url} size={40} />
                        <div className="min-w-0">
                            <p className="m-0 font-bold text-sm truncate" style={{ color: C.text }}>
                                {staff.first_name} {staff.last_name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                                <p className="m-0 text-xs" style={{ color: C.muted }}>
                                    {staff.role} · {staff.employment_type}
                                </p>
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                    style={{ background: C.amber + '22', color: C.amber }}>
                                    {period}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                            fill="none" stroke={C.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-5">

                    {/* Attendance KPIs */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            Attendance — {period}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                            <KPI label="Present" value={present} color={C.green}  sub="on time"      />
                            <KPI label="Late"  value={late} color={C.amber}  sub="arrived late" />
                            <KPI label="Absent" value={absent} color={C.coral}  sub="no-shows"     />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                            <KPI label="On Leave" value={onLeave}              color={C.purple} />
                            <KPI label="Total Hours" value={fmtDuration(totalMs)} color={C.blue}   />
                            <KPI label="Att. Rate" value={`${rate}%`}
                                color={rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral} />
                        </div>
                        <div className="mt-3 rounded-xl p-3 border" style={{ borderColor: C.border, background: C.dark }}>
                            <div className="flex justify-between mb-1.5">
                                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.muted }}>
                                    Attendance Rate
                                </span>
                                <span className="text-xs font-bold"
                                    style={{ color: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }}>
                                    {rate}%
                                </span>
                            </div>
                            <div className="h-2 rounded-full" style={{ background: C.border }}>
                                <div className="h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${rate}%`, background: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }} />
                            </div>
                        </div>
                    </div>

                    {/* Leave Scheduling */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-bold uppercase tracking-widest m-0" style={{ color: C.muted }}>
                                Schedule Leave
                            </p>
                            <button
                                onClick={() => { setShowLeaveForm(v => !v); setLeaveError(null) }}
                                className="text-[11px] px-3 py-1 rounded-full border font-semibold transition-colors"
                                style={{
                                    borderColor: showLeaveForm ? C.amber : C.border,
                                    color:       showLeaveForm ? C.amber : C.textSoft,
                                    background:  C.card,
                                    cursor: 'pointer',
                                }}>
                                {showLeaveForm ? '✕ Cancel' : '+ Add Leave'}
                            </button>
                        </div>

                        {leaveError && (
                            <div className="rounded-lg px-3 py-2 text-[11px] mb-3"
                                style={{ background: C.coral + '12', color: C.coral }}>
                                ⚠ {leaveError}
                            </div>
                        )}

                        {showLeaveForm && (
                            <div className="rounded-xl border p-4 flex flex-col gap-3"
                                style={{ borderColor: C.border, background: C.dark }}>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[11px] font-semibold mb-1" style={{ color: C.muted }}>
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={leaveForm.start_date}
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={e => setLeaveForm(f => ({ ...f, start_date: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg border text-xs outline-none"
                                            style={{ borderColor: C.border, color: C.text, background: C.card }}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold mb-1" style={{ color: C.muted }}>
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            value={leaveForm.end_date}
                                            min={leaveForm.start_date}
                                            onChange={e => setLeaveForm(f => ({ ...f, end_date: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg border text-xs outline-none"
                                            style={{ borderColor: C.border, color: C.text, background: C.card }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold mb-1" style={{ color: C.muted }}>
                                        Reason (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={leaveForm.reason}
                                        placeholder="e.g. Sick leave, vacation…"
                                        onChange={e => setLeaveForm(f => ({ ...f, reason: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg border text-xs outline-none"
                                        style={{ borderColor: C.border, color: C.text, background: C.card }}
                                    />
                                </div>
                                <div className="rounded-lg px-3 py-2 text-[11px]"
                                    style={{ background: C.purple + '12', color: C.purple }}>
                                    This will mark each day in the selected range as <strong>On Leave</strong> in the attendance records. Existing clock-in records will be overwritten.
                                </div>
                                <button
                                    onClick={handleSaveLeave}
                                    disabled={savingLeave || !leaveForm.start_date || !leaveForm.end_date}
                                    className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-opacity"
                                    style={{ background: C.purple, cursor: 'pointer' }}>
                                    {savingLeave ? 'Saving…' : 'Confirm Leave'}
                                </button>
                            </div>
                        )}
                        {/* ── Upcoming Leaves ── */}
                        {(() => {
                            const today = getLocalDateStr()
                            const upcoming = (attendance ?? [])
                                .filter(r => r.status === 'on_leave' && r.date >= today)
                                .sort((a, b) => a.date.localeCompare(b.date))
                            if (upcoming.length === 0) return null
                            return (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                                        Upcoming Leaves ({upcoming.length})
                                    </p>
                                    <div className="flex flex-col gap-2">
                                        {upcoming.map(r => (
                                            <div key={r.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border"
                                                style={{ borderColor: C.border, background: C.purple + '08' }}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                                                        style={{ background: C.purple + '22', color: C.purple }}>
                                                        On Leave
                                                    </span>
                                                    <span className="text-xs font-medium" style={{ color: C.text }}>
                                                        {fmtDate(r.date)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {r.notes && (
                                                        <span className="text-[11px]" style={{ color: C.textSoft }}>{r.notes}</span>
                                                    )}
                                                    <button
                                                        onClick={async () => {
                                                            const result = await Swal.fire({
                                                                title: 'Remove Leave?',
                                                                text: `This will remove the leave scheduled for ${fmtDate(r.date)}.`,
                                                                icon: 'warning',
                                                                showCancelButton: true,
                                                                confirmButtonColor: '#dc2626',
                                                                cancelButtonColor: '#6b7280',
                                                                confirmButtonText: 'Yes, remove',
                                                                cancelButtonText: 'Cancel'
                                                            })
                                                            if (result.isConfirmed) handleDeleteLeave(r)
                                                        }}
                                                        disabled={deletingLeaveId === r.id}
                                                        className="text-[10px] px-2 py-0.5 rounded-full border font-semibold transition-colors disabled:opacity-40"
                                                        style={{
                                                            borderColor: C.coral,
                                                            color: C.coral,
                                                            background: C.coral + '10',
                                                            cursor: 'pointer',
                                                        }}>
                                                        {deletingLeaveId === r.id ? '…' : '✕ Remove'}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        })()}
                    </div>

                    {/* Order KPIs */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            Orders Managed — {period}
                        </p>
                        {isBarista ? (
                            <div className="grid grid-cols-2 gap-2">
                                <KPI label="Orders Completed" value={orders?.count ?? 0} color={C.teal} sub="via order_completed_by" />
                                <KPI label="Revenue Handled" value={fmtCurrency(orders?.revenue ?? 0)} color={C.blue} sub="from completed orders" />
                                <KPI label="Avg Order"
                                    value={(orders?.count ?? 0) > 0 ? fmtCurrency((orders?.revenue ?? 0) / orders.count) : '—'}
                                    color={C.amber} sub="per order" />
                                <KPI label="POS Handled" value={pos?.count ?? 0} color={C.purple} sub="via pos_processed_by" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                <KPI label="Orders Processed" value={processed?.orderCount ?? 0} color={C.teal} sub="unique orders" />
                                <KPI label="Items Prepared" value={processed?.count ?? 0} color={C.green} sub="items marked ready" />
                                <KPI label="Revenue Handled" value={fmtCurrency(processed?.revenue ?? 0)} color={C.blue} sub="from those orders" />
                                <KPI label="Avg Order"
                                    value={processed?.orderCount ? fmtCurrency((processed?.revenue ?? 0) / processed.orderCount) : '—'}
                                    color={C.amber} sub="per order" />
                            </div>
                        )}
                    </div>

                    {/* POS KPIs */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            POS Transactions — {period}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            <KPI label="POS Transactions" value={pos?.count ?? 0}               color={C.purple} />
                            <KPI label="POS Revenue" value={fmtCurrency(pos?.revenue ?? 0)} color={C.green}  />
                            <KPI label="Avg POS"
                                value={pos?.count ? fmtCurrency((pos.revenue ?? 0) / pos.count) : '—'}
                                color={C.teal} />
                        </div>
                    </div>

                    {/* Recent history — always last 10, unaffected by range filter */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.muted }}>
                            Recent Attendance (Last 10 Records)
                        </p>
                        <div className="overflow-x-auto rounded-xl border" style={{ borderColor: C.border }}>
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr style={{ background: C.dark }}>
                                        {['Date','Status','Clock In','Clock Out','Hours','OT/UT','Proof'].map(h => (
                                            <th key={h} className="text-left py-2 px-3 font-semibold uppercase tracking-wide"
                                                style={{ color: C.muted, fontSize: 10 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedHistory.length === 0 && (
                                        <tr><td colSpan={7} className="py-6 text-center text-xs" style={{ color: C.muted }}>
                                            No records found.
                                        </td></tr>
                                    )}
                                    {sortedHistory.map(r => {
                                        const dur = r.clock_in && r.clock_out
                                            ? new Date(r.clock_out) - new Date(r.clock_in) : null
                                        return (
                                            <tr key={r.id} className="hover:bg-gray-50 transition-colors"
                                                style={{ borderTop: `1px solid ${C.border}` }}>
                                                <td className="py-2 px-3 font-medium" style={{ color: C.text }}>{fmtDate(r.date)}</td>
                                                <td className="py-2 px-3">
                                                    <StatusChip status={
                                                        r.clock_in
                                                            ? r.status
                                                            : r.status === 'on_leave'
                                                                ? 'on_leave'
                                                                : isDayOff(r.date, schedule)
                                                                    ? 'day_off'
                                                                    : deriveTodayStatus(r, new Date(), schedule) ?? r.status
                                                    } />
                                                </td>
                                                <td className="py-2 px-3" style={{ color: C.blue }}>{fmtTime(r.clock_in)}</td>
                                                <td className="py-2 px-3" style={{ color: C.teal }}>{fmtTime(r.clock_out)}</td>
                                                <td className="py-2 px-3 font-semibold" style={{ color: C.green }}>{fmtDuration(dur)}</td>
                                                <td className="py-2 px-3"><OvertimeChip record={r} /></td>
                                                <td className="py-2 px-3">
                                                    <div className="flex items-center gap-1.5">
                                                        {r.clock_in_photo ? (
                                                            <button onClick={() => onViewPhoto?.({ url: r.clock_in_photo, label: 'Clock-In Photo' })} title="Clock-in photo">
                                                                <img src={r.clock_in_photo} alt="in"
                                                                    className="w-7 h-7 rounded object-cover border hover:opacity-75 transition-opacity"
                                                                    style={{ borderColor: C.green }} />
                                                            </button>
                                                        ) : (
                                                            <div className="w-7 h-7 rounded border flex items-center justify-center"
                                                                style={{ borderColor: C.border }}>
                                                                <span style={{ color: C.muted, fontSize: 8 }}>—</span>
                                                            </div>
                                                        )}
                                                        {r.clock_out_photo ? (
                                                            <button onClick={() => onViewPhoto?.({ url: r.clock_out_photo, label: 'Clock-Out Photo' })} title="Clock-out photo">
                                                                <img src={r.clock_out_photo} alt="out"
                                                                    className="w-7 h-7 rounded object-cover border hover:opacity-75 transition-opacity"
                                                                    style={{ borderColor: C.coral }} />
                                                            </button>
                                                        ) : (
                                                            <div className="w-7 h-7 rounded border flex items-center justify-center"
                                                                style={{ borderColor: C.border }}>
                                                                <span style={{ color: C.muted, fontSize: 8 }}>—</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ── Staff row card ─────────────────────────────────────────────────────────
function StaffRow({ staff, attendance, orders, pos, schedule, onViewDetails, startBound, endBound }) {
    const today = getLocalDateStr()
    const todayRecord = attendance?.find(r => r.date === today)

    // Attendance stats filtered to the selected window
    const windowAtt = useMemo(() =>
        filterByDate(attendance ?? [], 'date', startBound, endBound),
    [attendance, startBound, endBound])

    const present  = windowAtt.filter(r => r.status === 'present').length
    const late     = windowAtt.filter(r => r.status === 'late').length
    const absent   = windowAtt.filter(r => r.status === 'absent').length
    const worked   = present + late
    const rate     = windowAtt.length ? Math.round((worked / windowAtt.length) * 100) : 0
    const totalMs  = windowAtt
        .filter(r => r.clock_in && r.clock_out)
        .reduce((s, r) => s + (new Date(r.clock_out) - new Date(r.clock_in)), 0)

    const statusColor = staff.status === 'Active' ? C.green : C.muted

    return (
        <div className="rounded-2xl border p-4 hover:shadow-md transition-shadow cursor-pointer"
            style={{ background: C.card, borderColor: C.border }}
            onClick={onViewDetails}>

            {/* Top row */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Avatar firstName={staff.first_name} lastName={staff.last_name}
                        profilePic={staff.profile_picture_url} size={40} />
                    <div>
                        <p className="m-0 font-bold text-sm" style={{ color: C.text }}>
                            {staff.first_name} {staff.last_name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[11px]" style={{ color: C.muted }}>{staff.role}</span>
                            <span className="w-1 h-1 rounded-full inline-block" style={{ background: C.border }} />
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                                style={{ background: statusColor + '18', color: statusColor }}>
                                {staff.status}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                    {todayRecord ? (
                        <StatusChip status={
                            !todayRecord.clock_in
                                ? deriveTodayStatus(todayRecord, new Date(), schedule)
                                : todayRecord.status
                        } />
                    ) : (
                        <StatusChip status={isDayOff(today, schedule) ? 'day_off' : 'waiting'} />
                    )}
                </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {/* Attendance */}
                <div className="col-span-2 rounded-xl p-3 border min-w-0" style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: C.muted }}>
                        Attendance
                    </p>
                    <div className="flex items-baseline gap-1 mb-1.5">
                        <span className="text-lg font-bold"
                            style={{ color: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }}>
                            {rate}%
                        </span>
                        <span className="text-[11px]" style={{ color: C.muted }}>
                            {windowAtt.length > 0 ? `${windowAtt.length} day${windowAtt.length !== 1 ? 's' : ''}` : 'no data'}
                        </span>
                    </div>
                    <div className="h-1.5 rounded-full mb-2" style={{ background: C.border }}>
                        <div className="h-1.5 rounded-full"
                            style={{ width: `${rate}%`, background: rate >= 90 ? C.green : rate >= 70 ? C.amber : C.coral }} />
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                        <span className="text-[10px]" style={{ color: C.green }}>{present} present</span>
                        <span className="text-[10px]" style={{ color: C.amber }}>{late} late</span>
                        <span className="text-[10px]" style={{ color: C.coral }}>{absent} absent</span>
                    </div>
                </div>

                {/* Hours */}
                <div className="rounded-xl p-3 border flex flex-col justify-between min-w-0"
                    style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.muted }}>Hours</p>
                    <p className="m-0 text-lg font-bold" style={{ color: C.blue }}>{fmtDuration(totalMs)}</p>
                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>worked</p>
                </div>

                {/* Orders */}
                <div className="rounded-xl p-3 border flex flex-col justify-between min-w-0"
                    style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.muted }}>Orders</p>
                    <p className="m-0 text-lg font-bold" style={{ color: C.amber }}>{orders?.count ?? 0}</p>
                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>{fmtCurrency(orders?.revenue ?? 0)}</p>
                </div>

                {/* POS */}
                <div className="rounded-xl p-3 border flex flex-col justify-between min-w-0"
                    style={{ borderColor: C.border, background: C.dark }}>
                    <p className="m-0 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.muted }}>POS</p>
                    <p className="m-0 text-lg font-bold" style={{ color: C.purple }}>{pos?.count ?? 0}</p>
                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>{fmtCurrency(pos?.revenue ?? 0)}</p>
                </div>
            </div>
        </div>
    )
}

export default function StaffAnalytics({ adminStaffData }) {

    const [staffList, setStaffList] = useState([])
    const [rawAttendanceMap, setRawAttendanceMap] = useState({})
    const [rawOrders, setRawOrders] = useState([])
    const [rawProcessedItems, setRawProcessedItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [scheduleMap, setScheduleMap] = useState({})

    const [rawBaristaAttendanceMap, setRawBaristaAttendanceMap] = useState({})
    const [baristaList, setBaristaList] = useState([])
    const [rawBaristaOrders, setRawBaristaOrders] = useState([])

    const [selectedStaff, setSelectedStaff] = useState(null)
    const [drawerAttendance, setDrawerAttendance] = useState([])
    const [viewPhoto, setViewPhoto] = useState(null)
    const [searchQ, setSearchQ] = useState('')
    const [filterStatus, setFilterStatus] = useState('all')
    const [sortBy, setSortBy] = useState('name')
    const [leaveError, setLeaveError] = useState(null)

    const [range, setRange] = useState('all')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')

    const [baristaScheduleMap, setBaristaScheduleMap] = useState({})

    const { startBound, endBound } = useDateBounds(range, customStart, customEnd)

    useEffect(() => {
        if (!adminStaffData?.branch_id) return
        fetchAll()
    }, [adminStaffData?.branch_id])

    const fetchAll = async () => {
        setLoading(true)
        const branchId = adminStaffData.branch_id

       const { data: staffDataRaw } = await supabase
            .from('staff')
            .select('id, first_name, last_name, role, employment_type, status, branch_id')
            .eq('branch_id', branchId)
            .order('first_name', { ascending: true })

        const staffData = (staffDataRaw ?? []).filter(s => s.role !== 'Barista')

        if (!staffData?.length) { setLoading(false); return }

        const staffIds = staffData.map(s => s.id)

        // Fetch baristas for this branch
        const { data: baristaData } = await supabase
            .from('barista_info')
            .select('barista_id, first_name, last_name, status, branch_id')
            .eq('branch_id', branchId)
            .eq('status', 'Active')

        const baristaIds = (baristaData ?? []).map(b => b.barista_id)

        const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id, profile_picture_url')
            .in('user_id', staffIds)
            .eq('user_type', 'staff')

        const picMap = Object.fromEntries((profiles ?? []).map(p => [p.user_id, p.profile_picture_url]))
        setStaffList(staffData.map(s => ({ ...s, profile_picture_url: picMap[s.id] ?? null })))

        const { data: attData } = await supabase
            .from('staff_attendance')
            .select('*')
            .in('staff_id', staffIds)
            .order('date', { ascending: false })

        // Fetch barista attendance records (stored under barista_id column)
        const { data: baristaAttData } = await supabase
            .from('staff_attendance')
            .select('*')
            .in('barista_id', baristaIds)
            .order('date', { ascending: false })

        const baristaAttMap = {}
        baristaIds.forEach(id => { baristaAttMap[id] = [] })
        ;(baristaAttData ?? []).forEach(r => {
            if (r.barista_id) baristaAttMap[r.barista_id]?.push(r)
        })
        setBaristaList(baristaData ?? [])
        setRawBaristaAttendanceMap(baristaAttMap)

        const { data: schedulesData } = await supabase
            .from('staff_schedules')
            .select('staff_id, work_days, default_scheduled_in, default_scheduled_out')
            .in('staff_id', staffIds)
        setScheduleMap(Object.fromEntries((schedulesData ?? []).map(s => [s.staff_id, s])))

        const { data: baristaSchedulesData } = await supabase
            .from('staff_schedules')
            .select('barista_id, work_days, default_scheduled_in, default_scheduled_out')
            .in('barista_id', baristaIds)
        setBaristaScheduleMap(Object.fromEntries((baristaSchedulesData ?? []).map(s => [s.barista_id, s])))

        const aMap = {}
        staffIds.forEach(id => { aMap[id] = [] })
        ;(attData ?? []).forEach(r => { aMap[r.staff_id]?.push(r) })
        setRawAttendanceMap(aMap)

        const { data: allOrders } = await supabase
            .from('orders')
            .select('order_id, staff_id, final_amount, order_source, created_at')
            .in('staff_id', staffIds)
            .eq('branch_id', branchId)
            .eq('order_status', 'completed')
            .order('created_at', { ascending: false })

        setRawOrders(allOrders ?? [])

        let baristaOrders = []
        if (baristaIds.length > 0) {
            const { data, error } = await supabase
                .from('orders')
                .select('order_id, barista_id, pos_processed_by, order_completed_by, final_amount, order_source, created_at')
                .eq('branch_id', branchId)
                .eq('order_status', 'completed')
                .or(`barista_id.in.(${baristaIds.join(',')}),pos_processed_by.in.(${baristaIds.join(',')}),order_completed_by.in.(${baristaIds.join(',')})`)
                .order('created_at', { ascending: false })
            if (error) console.error('Barista orders fetch error:', error)
            baristaOrders = data ?? []
        }
        setRawBaristaOrders(baristaOrders)

        const { data: processedItems } = await supabase
            .from('order_items')
            .select('order_item_id, processed_by, order_id, orders!inner(final_amount, created_at, branch_id)')
            .in('processed_by', staffIds)
            .eq('orders.branch_id', branchId)
            .eq('orders.order_status', 'completed')

        setRawProcessedItems(processedItems ?? [])

        setLoading(false)
    }

    const staffIds = useMemo(() => staffList.map(s => s.id), [staffList])

    const baristaIds = useMemo(() => baristaList.map(b => b.barista_id), [baristaList])

    const { baristaOrdersMap, baristaPosMap } = useMemo(() => {
        const filtered = filterByDate(rawBaristaOrders, 'created_at', startBound, endBound)
        const { prepMap, posMap } = buildBaristaOrderMaps(baristaIds, filtered)
        return { baristaOrdersMap: prepMap, baristaPosMap: posMap }
    }, [rawBaristaOrders, baristaIds, startBound, endBound])

    const attendanceMap = useMemo(() => {
        const result = {}
        staffIds.forEach(id => {
            result[id] = filterByDate(rawAttendanceMap[id] ?? [], 'date', startBound, endBound)
        })
        return result
    }, [rawAttendanceMap, staffIds, startBound, endBound])

    const { ordersMap, posMap, processedMap } = useMemo(() => {
        const filtered = filterByDate(rawOrders, 'created_at', startBound, endBound)
        const { oMap, pMap } = buildOrderMaps(staffIds, filtered)

        const prMap = {}
            staffIds.forEach(id => { prMap[id] = { count: 0, revenue: 0, seenOrderIds: new Set() } })

            const filteredProcessed = filterByDate(rawProcessedItems, 'orders.created_at', startBound, endBound)
            filteredProcessed.forEach(item => {
                if (!item.processed_by) return
                if (!prMap[item.processed_by]) return

                const entry = prMap[item.processed_by]

                entry.count += 1

                if (!entry.seenOrderIds.has(item.order_id)) {
                    entry.seenOrderIds.add(item.order_id)
                    entry.revenue += Number(item.orders?.final_amount ?? 0)
                }
            })

            staffIds.forEach(id => {
                prMap[id].orderCount = prMap[id].seenOrderIds.size
                delete prMap[id].seenOrderIds
            })

        return { ordersMap: oMap, posMap: pMap, processedMap: prMap }
    }, [rawOrders, rawProcessedItems, staffIds, startBound, endBound])

    const openDrawer = async (staff) => {
        setSelectedStaff(staff)
        const { data } = await supabase
            .from('staff_attendance')
            .select('*')
            .eq('staff_id', staff.id)
            .order('date', { ascending: false })
        setDrawerAttendance(data ?? [])
    }

    const openBaristaDrawer = async (barista) => {
        setSelectedStaff({
            ...barista,
            id: barista.barista_id,
            role: 'Barista',
            employment_type: barista.employment_type ?? '',
            profile_picture_url: null,
            branch_id: barista.branch_id,
        })
        const { data } = await supabase
            .from('staff_attendance')
            .select('*')
            .eq('barista_id', barista.barista_id)
            .order('date', { ascending: false })
        setDrawerAttendance(data ?? [])
    }

    // Re-fetch this person's attendance from the DB and push it back into
    // both the drawer AND the raw map used by the staff cards / branch
    // summary — so add/remove leave shows up everywhere immediately,
    // instead of only after closing and reopening the drawer.
    const refetchDrawerAttendance = async (staffId, isBarista) => {
        const idCol = isBarista ? 'barista_id' : 'staff_id'
        const { data } = await supabase
            .from('staff_attendance')
            .select('*')
            .eq(idCol, staffId)
            .order('date', { ascending: false })
        const fresh = data ?? []
        setDrawerAttendance(fresh)
        if (isBarista) {
            setRawBaristaAttendanceMap(prev => ({ ...prev, [staffId]: fresh }))
        } else {
            setRawAttendanceMap(prev => ({ ...prev, [staffId]: fresh }))
        }
    }

    const filtered = useMemo(() => staffList.filter(s => {
        const matchSearch = searchQ === '' ||
            `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchQ.toLowerCase()) ||
            s.role.toLowerCase().includes(searchQ.toLowerCase())
        const matchStatus = filterStatus === 'all' || s.role === filterStatus
        return matchSearch && matchStatus
    }), [staffList, searchQ, filterStatus])

    const sorted = useMemo(() => [...filtered].sort((a, b) => {
        if (sortBy === 'name')       return a.first_name.localeCompare(b.first_name)
        if (sortBy === 'attendance') {
            const rate = s => {
                const m = attendanceMap[s.id] ?? []
                const worked = m.filter(r => r.status === 'present' || r.status === 'late').length
                return m.length ? worked / m.length : 0
            }
            return rate(b) - rate(a)
        }
        if (sortBy === 'orders') return (ordersMap[b.id]?.count ?? 0) - (ordersMap[a.id]?.count ?? 0)
        if (sortBy === 'pos')    return (posMap[b.id]?.count    ?? 0) - (posMap[a.id]?.count    ?? 0)
        return 0
    }), [filtered, sortBy, attendanceMap, ordersMap, posMap])

    if (loading) {
        return (
            <div className="p-6">
                <Card><Spinner height={300} /></Card>
            </div>
        )
    }

    return (
        <div className="p-6 font-sans flex flex-col gap-5">

            {viewPhoto && (
                <PhotoModal url={viewPhoto.url} label={viewPhoto.label} onClose={() => setViewPhoto(null)} />
            )}

            {selectedStaff && (() => {
                const isBarista = selectedStaff.role === 'Barista'
                return (
                    <StaffDrawer
                        staff={selectedStaff}
                        attendance={drawerAttendance}
                        schedule={scheduleMap[selectedStaff.id] ?? null}
                        orders={isBarista ? baristaOrdersMap[selectedStaff.id] : ordersMap[selectedStaff.id]}
                        pos={isBarista ? baristaPosMap[selectedStaff.id] : posMap[selectedStaff.id]}
                        processed={isBarista ? undefined : processedMap[selectedStaff.id]}
                        isBarista={isBarista}
                        onClose={() => setSelectedStaff(null)}
                        onRefetch={() => refetchDrawerAttendance(selectedStaff.id, isBarista)}
                        startBound={startBound}
                        endBound={endBound}
                        onViewPhoto={setViewPhoto}
                    />
                )
            })()}

            {/* Branch summary */}
            {(staffList.length > 0 || baristaList.length > 0) && (
                <>
                    <BranchSummaryBar
                        staffList={staffList}
                        baristaList={baristaList}
                        rawAttendanceMap={rawAttendanceMap}
                        rawBaristaAttendanceMap={rawBaristaAttendanceMap}
                        ordersMap={ordersMap}
                        baristaOrdersMap={baristaOrdersMap}
                        posMap={posMap}
                        baristaPosMap={baristaPosMap}
                        startBound={startBound}
                        endBound={endBound}
                    />
                    <ActivePOSBadge branchId={adminStaffData?.branch_id} />
                </>
            )}

            <TopRemakeOffendersCard
                branchId={adminStaffData?.branch_id}
                startBound={startBound}
                endBound={endBound}
            />

            {/* Filters bar */}
            <Card>
                <div className="flex flex-col gap-3">

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[200px]">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <input
                                value={searchQ}
                                onChange={e => setSearchQ(e.target.value)}
                                placeholder="Search by name or role…"
                                className="w-full pl-9 pr-4 py-2 rounded-xl border text-sm outline-none"
                                style={{ borderColor: C.border, color: C.text, background: C.dark }} />
                        </div>

                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                            className="px-3 py-2 rounded-xl border text-sm outline-none"
                            style={{ borderColor: C.border, color: C.text, background: C.dark }}>
                            <option value="all">All roles</option>
                            {[...new Set(staffList.map(s => s.role).filter(Boolean))].sort().map(role => (
                                <option key={role} value={role}>{role}</option>
                            ))}
                            {baristaList.length > 0 && <option value="Barista">Barista</option>}
                        </select>

                        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                            className="px-3 py-2 rounded-xl border text-sm outline-none"
                            style={{ borderColor: C.border, color: C.text, background: C.dark }}>
                            <option value="name">Sort: Name</option>
                            <option value="attendance">Sort: Attendance</option>
                            <option value="orders">Sort: Orders</option>
                            <option value="pos">Sort: POS Transaction</option>
                        </select>

                        <span className="text-xs" style={{ color: C.muted }}>
                            {sorted.length} staff member{sorted.length !== 1 ? 's' : ''}
                        </span>

                        <button onClick={fetchAll}
                            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-colors hover:bg-gray-100"
                            style={{ borderColor: C.border, color: C.textSoft }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10"/>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                            </svg>
                            Refresh
                        </button>
                    </div>

                    <div className="flex items-start justify-between flex-wrap gap-2 pt-3 border-t"
                        style={{ borderColor: C.border }}>
                        <div>
                            <p className="m-0 text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.muted }}>
                                Date Range
                            </p>
                            <p className="m-0 text-[11px] mt-0.5" style={{ color: C.textSoft }}>
                                Filters attendance and order stats across all cards
                            </p>
                        </div>
                        <RangeSelector
                            range={range}             setRange={setRange}
                            customStart={customStart} setCustomStart={setCustomStart}
                            customEnd={customEnd}     setCustomEnd={setCustomEnd}
                        />
                    </div>
                </div>
            </Card>

            {/* Staff cards */}
            {filterStatus !== 'Barista' && (sorted.length === 0 ? (
                <Card>
                    <div className="py-16 flex flex-col items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                            fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                        <p className="text-sm font-medium" style={{ color: C.muted }}>
                            {staffList.length === 0 ? 'No other staff in this branch.' : 'No staff match your filters.'}
                        </p>
                    </div>
                </Card>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {sorted.map(s => (
                        <StaffRow
                            key={s.id}
                            staff={s}
                            attendance={rawAttendanceMap[s.id] ?? []}
                            schedule={scheduleMap[s.id] ?? null}
                            orders={ordersMap[s.id]}
                            pos={posMap[s.id]}
                            onViewDetails={() => openDrawer(s)}
                            startBound={startBound}
                            endBound={endBound}
                        />
                   ))}
                </div>
            ))}

               {/* Barista Section */}
            {(filterStatus === 'all' || filterStatus === 'Barista') && baristaList.length > 0 && (() => {
                const filteredBaristas = baristaList.filter(b =>
                    searchQ === '' ||
                    `${b.first_name} ${b.last_name}`.toLowerCase().includes(searchQ.toLowerCase())
                )
                if (filteredBaristas.length === 0) return null
                return (
                <>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.muted }}>
                            Baristas ({filteredBaristas.length})
                        </p>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {filteredBaristas.map(b => (
                            <StaffRow
                                key={b.barista_id}
                                staff={{
                                    id:                  b.barista_id,
                                    first_name:          b.first_name,
                                    last_name:           b.last_name,
                                    role:                'Barista',
                                    status:              b.status,
                                    employment_type:     b.employment_type ?? '',
                                    profile_picture_url: null,
                                    branch_id:           b.branch_id,
                                }}
                                attendance={rawBaristaAttendanceMap[b.barista_id] ?? []}
                                schedule={baristaScheduleMap[b.barista_id] ?? null}
                                orders={baristaOrdersMap[b.barista_id]}
                                pos={baristaPosMap[b.barista_id]}
                                onViewDetails={() => openBaristaDrawer(b)}
                                startBound={startBound}
                                endBound={endBound}
                            />
                       ))}
                    </div>
                </>
                )
            })()}
        </div>
    )
}